import { ColorUpdater } from 'bulma-css-vars';
import { useEffect } from 'react';
import { useFirebase } from '../../firebase/context';
import { bulmaCssVariablesDefs } from '../../styles/bulma-generated/bulma-colors';
import { useUnsafeInjectHtmlIntoHeadEffect } from './unsafeInjectHtmlIntoHead';
import { DEFAULT_OVERLAY_OPACITY } from '../../../../../functions/src/shared';

const Head = () => {
  const { publicConfig } = useFirebase();
  const {
    colors,
    fonts,
    favicon,
    background: backgroundImage,
    css,
    overlayOpacity,
  } = publicConfig?.theme ?? {};

  useUnsafeInjectHtmlIntoHeadEffect(publicConfig?.headInjectedHtml ?? '');

  useEffect(() => {
    // Update color custom css properties
    const colorUpdater = new ColorUpdater(bulmaCssVariablesDefs as any); // typescript didn't seem to like this

    for (const [name, color] of Object.entries(colors ?? {})) {
      const updatedColors = colorUpdater.getUpdatedVars(name, color.value);
      updatedColors.forEach(({ name, value }) => {
        document.documentElement.style.setProperty(name, value);
      });
    }

    // Update font families custom css properties
    for (const [name, font] of Object.entries(fonts ?? {})) {
      document.documentElement.style.setProperty(`--app-font-family--${name}`, font.value);
    }
  }, [colors, fonts]);

  // Generate custom font CSS for any uploaded custom fonts
  const customFontCSS = Object.entries(fonts ?? {})
    .map(([name, font]) => {
      if (font.provider !== 'custom' || !font.url) {
        return '';
      }

      const fontName = font.value;
      const fontUrl = font.url;
      const fontFormat = fontUrl.endsWith('.woff2')
        ? 'woff2'
        : fontUrl.endsWith('.woff')
        ? 'woff'
        : 'truetype';

      return `
        @font-face {
          font-family: '${fontName}';
          src: url('${process.env.REACT_APP_ASSET_URI ?? ''}${fontUrl}') format('${fontFormat}');
          font-weight: normal;
          font-style: normal;
        }
      `;
    })
    .join('\n');

  const defaultFontFamily =
    'BlinkMacSystemFont, -apple-system, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "Helvetica", "Arial", sans-serif';

  const themeStyles = `
    ${customFontCSS}
    
    :root {
      --app-font-family--primary: ${defaultFontFamily};
      --app-font-family--secondary: ${defaultFontFamily};
    }
    html {
      ${
        publicConfig?.theme?.backgroundMode === 'color'
          ? `background-color: ${publicConfig?.theme?.colors?.background?.value ?? ''};`
          : backgroundImage
          ? `background-image: url(${backgroundImage});`
          : `background-color: ${publicConfig?.theme?.colors?.background?.value ?? ''};`
      }
    }
    html::before {
      background-color: ${colors?.overlay?.value ?? 'rgba(0, 0, 0, 1)'};
      opacity: ${overlayOpacity ?? DEFAULT_OVERLAY_OPACITY};
    }
    ${css ? css : ''}
  `;

  const faviconIco = favicon?.ico ?? null;
  const faviconPng = favicon?.png ?? null;
  const appTitle = publicConfig?.appTitle ?? '';

  return (
    <>
      <title>{appTitle}</title>
      <meta charSet="utf-8" />
      <meta
        name="description"
        content={`${appTitle} - Return or exchange your purchased products.`}
      />
      <style type="text/css">{themeStyles}</style>
      {faviconIco && <link rel="shortcut icon" href={faviconIco} />}
      {faviconIco && <link rel="icon" sizes="16x16 32x32 64x64" href={faviconIco} />}
      {faviconPng && <link rel="apple-touch-icon" href={faviconPng} />}
    </>
  );
};

export default Head;
